/**
 * Admin Articles: Firestore docs + editors for cards, categories & pet tags refs.
 */

import { useRef, useState } from "react";
import { useMediaQuery } from "react-responsive";
import { useSelector } from "react-redux";
import {
  selectArticleError,
  selectArticleSaving,
} from "../../../features/articles/articlesSlice";
import {
  BackScreenDiv,
  Button,
  ContentDiv,
  Div,
  H1,
  HeaderDiv,
  ScreenDiv,
  TableColumnDiv,
  TableRowDivArticles,
  UserTableBodyDiv,
  UserTableDiv,
  UserTableRowDiv,
} from "../../../styles/styles";
import dayjs from "dayjs";
import { collection, doc } from "firebase/firestore";
import { db } from "../../../app/firebase";
import {
  normalizeCardsArray,
  isArticleCardNonempty,
} from "../../../utils/articleHelpers";
import { v4 as uuidv4 } from "uuid";
import { emptySiteForm } from "../../../utils/siteArticle";
import AdminArticleFormPanel, {
  ARTICLE_CREATE_FORM_ID,
  articleEditFormId,
} from "./adminArticleFormPanel";

const fmtPublishedDate = (ms) => {
  if (!ms || typeof ms !== "number") return "—";
  try {
    return dayjs(ms).format("YYYY-MM-DD");
  } catch {
    return "—";
  }
};

const emptyFormData = () => ({
  slug: { en: "", ru: "", sr: "" },
  title: { en: "", ru: "", sr: "" },
  subtitle: { en: "", ru: "", sr: "" },
  coverUrl: "",
  publishedAt: dayjs().format("YYYY-MM-DD"),
  categoryIds: [],
  petTagIds: [],
  cards: [],
  webOnly: false,
  mobileOnly: false,
  showOnSite: false,
  pinned: false,
  site: emptySiteForm(),
});

const formDataFromArticle = (article) => {
  const cards = normalizeCardsArray(article.cards).map((c) => ({
    id: typeof c.id === "string" && c.id ? c.id : uuidv4(),
    title: { en: "", ru: "", sr: "", ...(c.title || {}) },
    bodyHtml: { en: "", ru: "", sr: "", ...(c.bodyHtml || {}) },
    imageUrl: c.imageUrl || "",
  }));

  return {
    slug: article.slug || { en: "", ru: "", sr: "" },
    title: article.title || { en: "", ru: "", sr: "" },
    subtitle: article.subtitle || { en: "", ru: "", sr: "" },
    coverUrl: article.coverUrl || "",
    publishedAt: article.publishedAt
      ? dayjs(article.publishedAt).format("YYYY-MM-DD")
      : dayjs().format("YYYY-MM-DD"),
    categoryIds:
      Array.isArray(article.categoryIds) && article.categoryIds.length > 0
        ? [...article.categoryIds]
        : [],
    petTagIds:
      Array.isArray(article.petTagIds) && article.petTagIds.length > 0
        ? [...article.petTagIds]
        : [],
    cards,
    webOnly: Boolean(article.webOnly),
    mobileOnly: Boolean(article.mobileOnly),
    showOnSite: Boolean(article.showOnSite),
    pinned: Boolean(article.pinned),
    site: emptySiteForm(),
  };
};

export default function AdminArticles({
  articles,
  loading,
  error,
  onRefresh,
  articleCategories,
  articlePetTags,
}) {
  const isDesktop = useMediaQuery({ minWidth: "800px" });
  const saveError = useSelector(selectArticleError);
  const saving = useSelector(selectArticleSaving);

  const newFirestoreIdRef = useRef(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createFormData, setCreateFormData] = useState(emptyFormData);
  const [editingId, setEditingId] = useState(null);
  const [editFormData, setEditFormData] = useState(emptyFormData);
  const [onlyWithoutCards, setOnlyWithoutCards] = useState(false);

  const ensureNewFirestoreId = () => {
    if (!newFirestoreIdRef.current && db) {
      newFirestoreIdRef.current = doc(collection(db, "articles")).id;
    }
    return newFirestoreIdRef.current;
  };

  const createFirestoreKey = createOpen ? ensureNewFirestoreId() || "" : "";

  const openCreate = () => {
    setEditingId(null);
    newFirestoreIdRef.current = null;
    ensureNewFirestoreId();
    setCreateFormData(emptyFormData());
    setCreateOpen(true);
  };

  const closeCreate = () => {
    setCreateOpen(false);
    newFirestoreIdRef.current = null;
    setCreateFormData(emptyFormData());
  };

  const handleEditToggle = (article) => {
    if (editingId === article.id) {
      setEditingId(null);
      setEditFormData(emptyFormData());
      return;
    }
    setCreateOpen(false);
    newFirestoreIdRef.current = null;
    setEditingId(article.id);
    setEditFormData(formDataFromArticle(article));
  };

  const handleCreateSaved = () => {
    closeCreate();
    onRefresh();
  };

  const handleEditSaved = () => {
    setEditingId(null);
    setEditFormData(emptyFormData());
    onRefresh();
  };

  const handleEditDeleted = () => {
    setEditingId(null);
    setEditFormData(emptyFormData());
    onRefresh();
  };

  const filteredList = articles.filter((a) => {
    if (!onlyWithoutCards) return true;
    const nc = normalizeCardsArray(a.cards);
    return !(nc.length && nc.some(isArticleCardNonempty));
  });

  return (
    <>
      <BackScreenDiv grayImage />

      <ScreenDiv column mobileJustifySpace>
        <HeaderDiv />

        <ContentDiv
          gap={!isDesktop && "5px"}
          paddingBack={isDesktop}
          borderRadius="30px"
          alignStart
          justifyStart
          height="auto"
          heightDesktop="auto"
        >
          <Div row width="100%" justify="space-between" align="center">
            {isDesktop && <H1>Articles</H1>}
            <Button onClick={onRefresh} disabled={loading} width="auto" greenButton>
              {loading ? "Loading…" : "Refresh"}
            </Button>
          </Div>

          {error && (
            <div role="alert" style={{ color: "#c62828", marginBottom: 8 }}>
              {error}
            </div>
          )}
          {saveError && (
            <div role="alert" style={{ color: "#c62828", marginBottom: 8 }}>
              {saveError}
            </div>
          )}

          <Div row gap="10px" justifyStart width="100%" height="auto">
            <Button
              type="button"
              greenButton
              width="auto"
              disabled={createOpen}
              onClick={openCreate}
            >
              Create article
            </Button>
            {createOpen ? (
              <>
                <Button
                  type="submit"
                  form={ARTICLE_CREATE_FORM_ID}
                  greenButton
                  width="auto"
                  disabled={saving}
                >
                  {saving ? "Saving…" : "Create"}
                </Button>
                <Button type="button" outlineButton width="auto" onClick={closeCreate}>
                  Close
                </Button>
              </>
            ) : null}
          </Div>

          {createOpen ? (
            <AdminArticleFormPanel
              title="Create New Article"
              formId={ARTICLE_CREATE_FORM_ID}
              formData={createFormData}
              setFormData={setCreateFormData}
              articleFirestoreKey={createFirestoreKey}
              articleId={null}
              onCancel={closeCreate}
              onSaved={handleCreateSaved}
              articleCategories={articleCategories}
              articlePetTags={articlePetTags}
            />
          ) : null}

          <Div row justifyStart gap="14px" width="100%" height="auto">
            <label>
              <input
                type="checkbox"
                checked={onlyWithoutCards}
                onChange={(e) => setOnlyWithoutCards(e.target.checked)}
              />{" "}
              Show articles without slides only (legacy)
            </label>
          </Div>

          <Div width="100%">
            <UserTableDiv>
              <TableRowDivArticles>
                <TableColumnDiv>Title</TableColumnDiv>
                <TableColumnDiv>Published</TableColumnDiv>
                <TableColumnDiv>Cards</TableColumnDiv>
                <TableColumnDiv>Site</TableColumnDiv>
                <TableColumnDiv>Edit</TableColumnDiv>
              </TableRowDivArticles>

              <UserTableBodyDiv>
                {filteredList.map((a) => {
                  const titlePreview =
                    a.title?.en || a.title?.ru || a.title?.sr || "—";
                  const hasCards = normalizeCardsArray(a.cards).some(
                    isArticleCardNonempty,
                  );
                  const isEditing = editingId === a.id;

                  return (
                    <UserTableRowDiv key={a.id}>
                      <TableRowDivArticles>
                        <TableColumnDiv>{titlePreview}</TableColumnDiv>
                        <TableColumnDiv>
                          {fmtPublishedDate(a.publishedAt)}
                        </TableColumnDiv>
                        <TableColumnDiv>{hasCards ? "yes" : "—"}</TableColumnDiv>
                        <TableColumnDiv>{a.showOnSite ? "yes" : "—"}</TableColumnDiv>
                        <TableColumnDiv>
                          <Div row gap="8px" justifyStart wrap="wrap">
                            {isEditing ? (
                              <Button
                                type="submit"
                                form={articleEditFormId(a.id)}
                                greenButton
                                width="auto"
                                fontSize="12px"
                                height="auto"
                                padding="5px 10px"
                                disabled={saving}
                              >
                                {saving ? "Saving…" : "Update"}
                              </Button>
                            ) : null}
                            <Button
                              type="button"
                              onClick={() => handleEditToggle(a)}
                              width="auto"
                              outlineButtonGreen
                              fontSize="12px"
                              height="auto"
                              padding="5px 10px"
                            >
                              {isEditing ? "Close" : "Edit"}
                            </Button>
                          </Div>
                        </TableColumnDiv>
                      </TableRowDivArticles>

                      {isEditing ? (
                        <AdminArticleFormPanel
                          title="Edit Article"
                          formId={articleEditFormId(a.id)}
                          formData={editFormData}
                          setFormData={setEditFormData}
                          articleFirestoreKey={a.id}
                          articleId={a.id}
                          onCancel={() => handleEditToggle(a)}
                          onSaved={handleEditSaved}
                          onDeleted={handleEditDeleted}
                          articleCategories={articleCategories}
                          articlePetTags={articlePetTags}
                        />
                      ) : null}
                    </UserTableRowDiv>
                  );
                })}
                {filteredList.length === 0 && (
                  <TableColumnDiv>No matching articles</TableColumnDiv>
                )}
              </UserTableBodyDiv>
            </UserTableDiv>
          </Div>
        </ContentDiv>
      </ScreenDiv>
    </>
  );
}
